import type { ButtonHTMLAttributes, ReactNode } from 'react'
import type { Visibility } from '../lib/types'

const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ')

/* ------------------------------------------------------------------ Button */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md'
}

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-sh)] font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
        variant === 'primary' && 'bg-accent text-white shadow-card hover:bg-accent-hover',
        variant === 'secondary' &&
          'border border-line bg-surface text-ink shadow-card hover:bg-sunken',
        variant === 'ghost' && 'text-muted hover:bg-sunken hover:text-ink',
        className,
      )}
    />
  )
}

/* ------------------------------------------------------------ Tag / badge */

export function Chip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count?: number
  active?: boolean
  onClick?: () => void
}) {
  const Tag = onClick ? 'button' : 'span'
  return (
    <Tag
      onClick={onClick}
      aria-pressed={onClick ? !!active : undefined}
      className={cx(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-mono text-xs',
        'transition-colors',
        active
          ? 'border-accent bg-accent-soft text-accent-text'
          : 'border-line bg-surface text-muted',
        onClick && !active && 'hover:border-line-strong hover:text-ink',
      )}
    >
      {label}
      {count !== undefined && <span className="text-subtle">{count}</span>}
    </Tag>
  )
}

export function VisibilityBadge({ visibility }: { visibility: Visibility }) {
  if (visibility === 'public') return null
  return (
    <span
      title="Internal — staff only"
      className="inline-flex items-center rounded-full bg-internal-soft px-2 py-0.5 text-[10px] font-semibold tracking-wide text-internal uppercase"
    >
      Internal
    </span>
  )
}

/* ----------------------------------------------------------------- States */

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-subtle" role="status">
      <span className="size-3.5 animate-spin rounded-full border-2 border-line border-t-accent" />
      {label}
    </span>
  )
}

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger'
  title?: string
  children: ReactNode
}) {
  return (
    <div
      className={cx(
        'rounded-[var(--radius-sh)] border-l-[3px] bg-sunken px-4 py-3 text-sm',
        tone === 'info' && 'border-l-accent',
        tone === 'warn' && 'border-l-internal',
        tone === 'danger' && 'border-l-danger',
      )}
    >
      {title && <p className="mb-1 font-semibold text-ink">{title}</p>}
      <div className="text-muted">{children}</div>
    </div>
  )
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sh)] border border-dashed border-line px-6 py-12 text-center">
      <p className="font-medium text-ink">{title}</p>
      {children && <p className="mt-1 text-sm text-subtle">{children}</p>}
    </div>
  )
}

export { cx }
